import { Injectable, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { AiService } from '../../../core/services/ai.service';
import { AiChatHistoryEntry } from '../../../core/models/ai.model';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  time: string;
  typing?: boolean;
}

export interface ActiveCase {
  caseId: string;
  reference: string;
  parties: string;
}

@Injectable({ providedIn: 'root' })
export class CaseChatService {
  /** Currently open case, null = drawer closed */
  readonly activeCase = signal<ActiveCase | null>(null);

  /** Full message thread for the active case */
  readonly messages = signal<ChatMessage[]>([]);

  /** True while index-case is in flight */
  readonly indexing = signal(false);

  /** True while an AI reply is streaming */
  readonly streaming = signal(false);

  constructor(private aiService: AiService, private snackBar: MatSnackBar) {}

  /** Open the drawer for a given case, indexing it and loading chat history in parallel */
  open(c: ActiveCase) {
    // If same case already open, just re-show the drawer
    if (this.activeCase()?.caseId === c.caseId) {
      this.activeCase.set(c);
      return;
    }

    // New case — reset and start loading
    this.activeCase.set(c);
    this.messages.set([]);
    this.indexing.set(true);

    forkJoin({
      index:   this.aiService.indexCase({ case_id: c.caseId }),
      history: this.aiService.getChatHistory(c.caseId, c.reference),
    }).subscribe({
      next: ({ history }) => {
        this.indexing.set(false);
        if (history.length) {
          this.loadHistory(history);
        } else {
          this.pushSystemIntro(c);
        }
      },
      error: () => {
        this.indexing.set(false);
        // Still open — server may already have it indexed; show intro
        this.pushSystemIntro(c);
        this.snackBar.open(
          'Case indexing failed. Responses may be limited.',
          'Dismiss',
          { duration: 5000 },
        );
      },
    });
  }

  close() {
    this.activeCase.set(null);
  }

  /** Send a user message and stream the AI reply via /chat */
  send(text: string) {
    if (!text.trim() || this.streaming()) return;

    const c = this.activeCase();
    if (!c) return;

    const now = this.timeNow();

    // Append user message
    this.messages.update((msgs) => [
      ...msgs,
      { role: 'user', text, time: now },
    ]);

    // Append typing placeholder (no text yet — shows animated dots)
    this.messages.update((msgs) => [
      ...msgs,
      { role: 'ai', text: '', time: now, typing: true },
    ]);

    this.streaming.set(true);

    this.aiService
      .chatStream(
        c.caseId,
        c.reference,
        text,
        // onChunk — append each arriving chunk to the last message
        (chunk) => {
          this.messages.update((msgs) => {
            const copy = [...msgs];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = {
              ...last,
              text: last.text + chunk,
              typing: true,
            };
            return copy;
          });
        },
        // onDone — seal the message (remove typing flag)
        () => {
          this.messages.update((msgs) => {
            const copy = [...msgs];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, typing: false };
            return copy;
          });
          this.streaming.set(false);
        },
        // onError — show snack, remove typing placeholder if still empty
        (err) => {
          console.error('[CaseChatService] stream error', err);
          this.messages.update((msgs) => {
            const copy = [...msgs];
            const last = copy[copy.length - 1];
            if (!last.text) {
              // Replace empty placeholder with a friendly error message
              copy[copy.length - 1] = {
                ...last,
                text: 'Sorry, I encountered an error processing your request. Please try again.',
                typing: false,
              };
            } else {
              copy[copy.length - 1] = { ...last, typing: false };
            }
            return copy;
          });
          this.streaming.set(false);
          this.snackBar.open('AI response failed. Please try again.', 'Dismiss', { duration: 4000 });
        },
      )
      .catch((err) => {
        // Catches errors thrown before the stream started (e.g. network offline)
        console.error('[CaseChatService] fetch error', err);
        this.streaming.set(false);
      });
  }

  // ── Helpers ──────────────────────────────────────────────────

  /**
   * Map history entries from the API into ChatMessage objects and set them
   * as the thread. The API uses role 'model' for AI; we use 'ai' internally.
   */
  private loadHistory(entries: AiChatHistoryEntry[]) {
    const now = this.timeNow();
    this.messages.set(
      entries.map((e) => ({
        role: e.role === 'model' ? 'ai' : 'user' as 'ai' | 'user',
        text: e.message,
        time: now,
      })),
    );
  }

  private pushSystemIntro(c: ActiveCase) {
    this.messages.set([
      {
        role: 'ai',
        text: `I have access to the complete case file for ${c.reference} — ${c.parties}. I can help you examine documents, identify legal issues, calculate timelines, and draft directions.\n\nWhat would you like to explore?`,
        time: this.timeNow(),
      },
    ]);
  }

  private timeNow(): string {
    return new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
}
